//
// A modal that collects a payment for ONE unpaid in-store order by showing a QR of the
// provider HOSTED-CHECKOUT URL the customer scans and pays on their own phone, terminal-
// free (take-order-scan-to-pay spec). The web twin of the mobile onCollectScanToPay flow
// (admin-mobile-app/App.js). It reuses the SAME three endpoints the mobile app calls —
//   POST /admin/pos/scan-to-pay/create -> { provider, sessionId, checkoutUrl, state, amount, currency, expiresAt }
//   POST /admin/pos/scan-to-pay/status -> { paymentStatus, scanToPayState, paid }
//   POST /admin/pos/scan-to-pay/cancel -> { ok, paid, scanToPayState }
// The amount is NEVER sent; the server re-prices from the order's authoritative total.
//
// Like the UPI modal (and UNLIKE the Stripe card sheet) this is ASYNCHRONOUS: the customer
// scans and pays out-of-band on the Stripe-hosted Checkout page, and Stripe's webhook marks
// the order paid. So this modal creates the session, renders a QR of the checkout URL, then
// POLLS the stored status until it reads paid (or the staff cancels / it times out). The
// browser NEVER marks the order paid — it only reflects the authoritative stored status it
// polls. "Cancel"/close expires a still-pending session (never un-pays a paid one).
//
// The QR is rendered client-side from the checkout URL with the pure-JS `qrcode` package
// (same dependency the mobile app uses), so no QR image is round-tripped from the backend.

import React, { useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import QRCode from 'qrcode';
import { friendlyApiError } from '../utils/apiError';
import { formatMinor } from '../utils/money';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

// Poll cadence + safety cap (mirrors the mobile 3s / ~10min).
const POLL_MS = 3000;
const MAX_POLLS = 200;

async function createScanToPaySession({ token, orderNumber, orderTimestamp }) {
  const res = await fetch(`${API_BASE_URL}/admin/pos/scan-to-pay/create`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ order_number: orderNumber, order_timestamp: orderTimestamp }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to create QR payment'));
  return data;
}

async function scanToPayStatus({ token, orderNumber, orderTimestamp }) {
  const res = await fetch(`${API_BASE_URL}/admin/pos/scan-to-pay/status`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ order_number: orderNumber, order_timestamp: orderTimestamp }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to fetch payment status'));
  return data;
}

async function cancelScanToPay({ token, orderNumber, orderTimestamp }) {
  const res = await fetch(`${API_BASE_URL}/admin/pos/scan-to-pay/cancel`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ order_number: orderNumber, order_timestamp: orderTimestamp }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to cancel QR payment'));
  return data;
}

export default function ScanToPayQrModal({ order, onClose, onPaid }) {
  const orderNumber = String(order?.order_number || '').trim();
  const orderTimestamp = order?.order_timestamp;

  // 'loading' -> 'waiting' -> 'paid' | 'error'
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [amountLabel, setAmountLabel] = useState('');

  const startedRef = useRef(false);
  const pollRef = useRef(null);
  // Latest phase, read inside async callbacks + the cleanup, without re-subscribing them.
  const phaseRef = useRef('loading');
  phaseRef.current = phase;

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    let cancelled = false;

    (async () => {
      try {
        if (!API_BASE_URL) throw new Error('Missing API base URL (REACT_APP_ADMIN_API_URL).');
        if (!orderNumber || orderTimestamp === undefined || orderTimestamp === null) {
          throw new Error('Missing order identifiers.');
        }

        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();

        // 1. Server creates the hosted-checkout session (amount re-priced server-side).
        const resp = await createScanToPaySession({ token, orderNumber, orderTimestamp });
        if (cancelled) return;
        const url = String(resp?.checkoutUrl || '').trim();
        if (!url) throw new Error('QR payment could not be created.');

        const amountMinor = Number(resp?.amount);
        const currency = String(resp?.currency || 'USD').toUpperCase();
        setAmountLabel(Number.isFinite(amountMinor) ? formatMinor(amountMinor, currency) : '');
        setCheckoutUrl(url);

        // Render the QR of the checkout URL to a data-URL <img>. A render failure is not
        // fatal: the raw link is still shown so staff can hand the phone the URL.
        try {
          const dataUrl = await QRCode.toDataURL(url, { width: 240, margin: 1 });
          if (!cancelled) setQrDataUrl(dataUrl);
        } catch {
          /* fall back to the link below */
        }
        setPhase('waiting');

        // 2. Poll stored status. The webhook is the writer; we only reflect it. Stop on
        //    paid, on unmount (cancelled), or after the safety cap.
        let ticks = 0;
        pollRef.current = setInterval(async () => {
          ticks += 1;
          if (cancelled || phaseRef.current === 'paid') {
            clearPoll();
            return;
          }
          if (ticks > MAX_POLLS) {
            clearPoll();
            return;
          }
          try {
            const freshSession = await Auth.currentSession();
            const freshToken = freshSession.getIdToken().getJwtToken();
            const st = await scanToPayStatus({ token: freshToken, orderNumber, orderTimestamp });
            if (!cancelled && st?.paid) {
              clearPoll();
              setPhase('paid');
              onPaid(true);
            }
          } catch {
            // Transient poll failure — keep polling; the webhook remains authoritative.
          }
        }, POLL_MS);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Could not start QR collection.');
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
      clearPoll();
      // Best-effort expire of a still-pending session when the modal unmounts without
      // payment, so a stale checkout is not left open. Never touches a paid order.
      if (phaseRef.current !== 'paid' && orderNumber && orderTimestamp != null) {
        (async () => {
          try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            await cancelScanToPay({ token, orderNumber, orderTimestamp });
          } catch {
            /* best-effort */
          }
        })();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber, orderTimestamp]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Collect payment by QR for order ${orderNumber}`}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{phase === 'paid' ? 'Payment received' : 'Scan to pay'}</h3>
          <span className="field-hint">Order {orderNumber}</span>
        </div>

        {phase === 'loading' ? (
          <div className="field-hint" style={{ padding: '16px 0' }}>Creating QR…</div>
        ) : null}

        {phase === 'error' ? (
          <>
            <div className="field-hint" style={{ color: 'rgba(153,27,27,0.95)', padding: '8px 0' }}>{error}</div>
            <button type="button" className="btn-secondary" onClick={onClose} style={{ width: '100%', marginTop: 8 }}>
              Close
            </button>
          </>
        ) : null}

        {phase === 'waiting' ? (
          <div style={{ textAlign: 'center' }}>
            {amountLabel ? (
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{amountLabel}</div>
            ) : null}
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt={`Payment QR code for order ${orderNumber}`}
                style={{ width: 240, height: 240, objectFit: 'contain', marginTop: 12 }}
              />
            ) : checkoutUrl ? (
              <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: 12, wordBreak: 'break-all' }}>
                {checkoutUrl}
              </a>
            ) : null}
            <div className="field-hint" style={{ marginTop: 12 }}>
              Ask the customer to scan with their phone camera and pay on the page that opens.
              Waiting for payment…
            </div>
            <button type="button" className="btn-secondary" onClick={onClose} style={{ width: '100%', marginTop: 16 }}>
              Cancel
            </button>
          </div>
        ) : null}

        {phase === 'paid' ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            {amountLabel ? (
              <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{amountLabel}</div>
            ) : null}
            <div style={{ fontSize: 44, marginTop: 8 }}>✅</div>
            <div style={{ marginTop: 8, fontSize: 16, fontWeight: 600, color: '#047857' }}>Paid</div>
            <button type="button" className="btn-primary" onClick={onClose} style={{ width: '100%', marginTop: 16 }}>
              Done
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
